import "./helpers/uploadDebugPreference";
import { createApp, watch } from "vue";
import App from "./App.vue";
import WebForm from "./WebForm.vue";
import WebFormV2 from "./WebFormV2.vue";
import AccountSetup from "./AccountSetup.vue";
import FreeTrial from "./components/FreeTrial.vue";
import vuetify from "./plugins/vuetify";
import { filterToastBeforeCreate } from "./helpers/toastSeverity";

/**
 * Determines which form component to render based on the form's version.
 * Version 2 forms use the new WebFormV2 component, older forms use WebForm.
 * Note: This must be called after window.env is initialized.
 */
async function getFormComponent() {
	try {
		const searchParams = new URLSearchParams(window.location.search);
		const isPreview = searchParams.get("preview") === "true";

		const hash = window.location.hash;
		if (hash) {
			const formId = hash.substring(1).split("?")[0]; // Remove query params from hash
			const data = await api.getFormInsecure(formId);
			// Version 2 forms use the new component
			if (data?.form?.version === 2) {
				return WebFormV2;
			}
		} else if (isPreview) {
			// If we are in preview mode but have no form ID, use WebFormV2
			// This happens when creating a new form
			return WebFormV2;
		}
	} catch (err) {
		console.error("Error fetching form version:", err);
	}
	// Default to old form component for version 1/null or on error
	return WebForm;
}
import auth, { auth as authInstance } from "./auth.js";
import router from "./router";
import "./assets/style.css";
import "./assets/styles/animation.css";
import "./assets/styles/liveswitch.css";
import "./assets/styles/page.scss";
import "./assets/styles/search.scss";
import "./assets/styles/settings-templates.scss";
import "./styles/list-page.scss";
import "vue-tel-input/vue-tel-input.css";
import api from "./api";
import ScribeMonitor from "./plugins/scribe-monitor";
import VueResizeText from "vue3-resize-text";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import "@common-ui/assets/css/feedbackToasts.css";
import trackDirective from "@/directives/track";
import testDirective from "@/directives/test";
import initializeDatadog from "./plugins/datadog";
import { Activator } from "@markerjs/markerjs3";
import { createPinia } from "pinia";
import BaseTextField from "@/components/formfields/BaseTextField.vue";
import BaseSelect from "@/components/formfields/BaseSelect.vue";
import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
import CharacterLimitedTextField from "@/components/formfields/CharacterLimitedTextField.vue";
import CharacterLimitedTextArea from "@/components/formfields/CharacterLimitedTextArea.vue";
import EmailTextField from "@/components/formfields/EmailTextField.vue";
import { installContactDebugController } from "@/helpers/contactDebug";
import VueTelInput from "vue-tel-input";
import { SafeVueTelInput } from "@/components/SafeVueTelInput";

// Polyfill for Array.at missing in iOS 15.2
import at from "array.prototype.at";

if (!Array.prototype.at) {
	Array.prototype.at = at;
}

installContactDebugController();

// import VuetifyGoogleAutocomplete from "vuetify-google-autocomplete"; // Assuming this was missing or implicitly global

const start = async function () {
	// set up process env and api
	const response = await fetch("/config/env.json");
	const env = await response.json();
	(window as any).api = api;
	(window as any).env = env;
	// Vue.prototype.env = env; // Moved to app config

	// Add the HubSpot chatbot script to index.html's body tag (but only for the demo environment).
	if (env.ENVIRONMENT === "demo") {
		const script = document.createElement("script");
		script.src = "//js.hs-scripts.com/3919779.js";
		document.body.appendChild(script);
	}

	// MarkerJS
	Activator.addKey("MJS3", env.VITE_MARKERJS_KEY);

	(window as any).PreferredCountries = ["US", "AU", "CA", "GB"];
	window.VueTelProps = {
		preferredCountries: (window as any).PreferredCountries,
		placeholder: "Phone Number (Required)",
		mode: "international",
		showDialCodeInSelection: true,
		autoDefaultCountry: true,
		dropdownOptions: {
			showFlags: true,
			showDialCodeInSelection: false,
			showSearchBox: true
		},
		inputOptions: {
			// LSP-15522: suppress Chrome's saved-phone dropdown across every <vue-tel-input>
			// in the app. The previous `tel` value was a semantic hint that Chrome interprets
			// as "please autofill" — fine for a Settings page where a user enters their own
			// phone, but it leaks personal phone history into customer-facing dialogs (Send
			// Invitation, EditUserDialog, WebFormV2). Default to off; override per-call-site
			// if a specific surface ever genuinely wants Chrome's tel-autofill back.
			autocomplete: "off",
			autofocus: true,
			id: "",
			name: "telephone",
			placeholder: "Phone Number (Required)",
			tabindex: "0",
			"aria-describedby": "",
			type: "tel"
		},
		disabledFormatting: false,
		wrapperClasses: "country-phone-input",
		validCharactersOnly: true
	};

	const configureApp = (app: any) => {
		app.config.globalProperties.env = env;

		// Datadog
		app.use(initializeDatadog);

		// app uses auth
		app.use(auth);

		// pinia
		const pinia = createPinia();
		app.use(pinia);

		// Vue.config.productionTip = false; // Not needed in V3
		app.use(VueResizeText);
		app.use(VueTelInput);
		app.component("vue-tel-input", SafeVueTelInput);

		// vuetify google autocomplete
		// app.use(VuetifyGoogleAutocomplete, { ... }); // TODO: Check compatibility

		app.use(Toast, {
			position: "top-center",
			closeOnClick: false,
			pauseOnHover: true,
			pauseOnFocusLoss: false,
			draggable: false,
			showCloseButtonOnHover: false,
			hideProgressBar: false,
			closeButton: "button",
			icon: true,
			rtl: false,
			maxToasts: 3,
			filterBeforeCreate: filterToastBeforeCreate,
			newestOnTop: true,
			shareAppContext: app
		});

		app.directive("track", trackDirective);
		app.directive("test", testDirective);

		// Globally register base form components
		app.component("BaseTextField", BaseTextField);
		app.component("BaseSelect", BaseSelect);
		app.component("BaseAutoComplete", BaseAutoComplete);
		app.component("CharacterLimitedTextField", CharacterLimitedTextField);
		app.component("CharacterLimitedTextArea", CharacterLimitedTextArea);
		app.component("EmailTextField", EmailTextField);
	};

	// allows us to bypass authentication and send the visitor
	// directly to the self service async form
	let app;
	const pathname = window.location.pathname;
	if (pathname == "/form") {
		// Determine which form version to render based on the form's version field
		const formComponent = await getFormComponent();
		app = createApp(formComponent);
		app.use(vuetify);
		configureApp(app);
		app.mount("#app");
	} else if (pathname == "/account-setup") {
		app = createApp(AccountSetup);
		app.use(vuetify);
		configureApp(app);
		app.mount("#app");
	} else if (pathname == "/free-trial") {
		app = createApp(FreeTrial);
		app.use(vuetify);
		configureApp(app);
		app.mount("#app");
	} else {
		// scribe monitoring after router is available (only in main app)
		// Vue.use(ScribeMonitor, router); // TODO: ScribeMonitor needs to be updated for app.use
		app = createApp(App);
		app.use(vuetify);
		app.use(router);
		configureApp(app);
		app.use(ScribeMonitor, router);
		app.mount("#app");
	}

	// watch for size to swap to mobile.
	// Note: accessing $vuetify on the app instance or global properties might be different in V3
	// Disabling these watchers for now as v.$vuetify is not available on app instance same way
	/*
	watch(
		() => v.$vuetify?.breakpoint?.smAndDown,
		(newVal) => {
			document.body.classList.toggle("mobile", newVal);
		},
		{ immediate: true }
	);

	watch(
		() => v.$vuetify?.breakpoint.name,
		(newVal) => {
			for (const cls of Array.from(document.body.classList)) {
				if (cls.startsWith("breakpoint--")) {
					document.body.classList.remove(cls);
				}
			}
			document.body.classList.add(`breakpoint--${newVal}`);
		},
		{ immediate: true }
	);
	*/

	const setOrientation = () => {
		const isLandscape = window.innerWidth > window.innerHeight;
		document.body.classList.toggle("landscape", isLandscape);
		document.body.classList.toggle("portrait", !isLandscape);
	};

	window.addEventListener(
		"resize",
		() => {
			setOrientation();
		},
		{ passive: true }
	);

	setOrientation();
};

start();
